import React, { useEffect, useState, useCallback } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';

const DISMISS_KEY = 'caraloom_pwa_dismissed_at';
const DISMISS_DAYS = 14;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true ||
  document.referrer.startsWith('android-app://');

const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

const recentlyDismissed = () => {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return ts && Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

/**
 * Installable-PWA prompt.
 * - Chromium/Android/desktop: captures `beforeinstallprompt` and shows a native install CTA.
 * - iOS Safari (no beforeinstallprompt): shows an "Add to Home Screen" hint.
 * Dismissals persist for 14 days. Hidden entirely when already installed.
 */
export const PwaInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS has no beforeinstallprompt — surface a manual hint after a short delay.
    let t;
    if (isIos()) {
      t = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 2500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (e) {
      /* noop */
    }
    setDeferred(null);
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  }, [deferred]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 z-[120] px-3 pointer-events-none"
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 76px)' }}
      data-testid="pwa-install-prompt"
    >
      <div className="pointer-events-auto mx-auto max-w-md rounded-2xl border border-cn-border bg-white shadow-xl shadow-cn-primary/10 p-3.5 flex items-center gap-3 animate-in slide-in-from-bottom-4 fade-in duration-300">
        <div className="w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br from-cn-primary to-cn-sage-600 flex items-center justify-center">
          <svg width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <path
              d="M9 8C5 8 3 12 5 16C7 20 11 22 16 22C21 22 25 20 27 16C29 12 27 8 23 8C19 8 17 12 16 16C15 20 13 24 9 24C5 24 3 20 5 16"
              stroke="#F7F5F0" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
            />
            <circle cx="16" cy="16" r="1.9" fill="#06B6D4" />
          </svg>
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-cn-text leading-tight">Install CaraLoom</p>
          {iosHint ? (
            <p className="text-xs text-cn-text-muted mt-0.5 flex items-center flex-wrap gap-x-1">
              Tap <Share className="inline w-3.5 h-3.5 text-cn-primary" /> then
              <span className="inline-flex items-center gap-0.5">&quot;Add to Home Screen&quot; <Plus className="inline w-3 h-3" /></span>
            </p>
          ) : (
            <p className="text-xs text-cn-text-muted mt-0.5">Add to your home screen for one-tap access.</p>
          )}
        </div>

        {!iosHint && (
          <button
            onClick={install}
            data-testid="pwa-install-btn"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-cn-primary text-white text-sm font-semibold px-4 py-2 hover:bg-cn-primary/90 transition-colors"
          >
            <Download className="w-4 h-4" /> Install
          </button>
        )}

        <button
          onClick={dismiss}
          data-testid="pwa-dismiss-btn"
          aria-label="Dismiss install prompt"
          className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-cn-text-muted hover:bg-cn-bg transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default PwaInstallPrompt;
